/** UI SmartLight — jeden ekran z sekcjami: konfiguracja → logowanie+PIN →
 * sprzedaż. Pasek stanu (tryb, wiek taryfy, pula, kolejka) jest widoczny ZAWSZE
 * — offline nie może wyglądać jak online, a stare dane jak świeże. */

import QRCode from "qrcode";
import jsQR from "jsqr";
import { kv, txns } from "./db";
import { Bundle, Passenger, OfferResult } from "./engine";
import * as api from "./api";
import * as vault from "./vault";
import * as sale from "./sale";
import * as sync from "./sync";
import { computeReadiness, Readiness } from "./readiness";

// Zbiór ikon rodziny (`design/mex-icons.js`, symlink + trasa `GET /mex-icons.js`,
// patrz `app.py`) — skrypt zwykły (nie moduł), ładowany w <head> PRZED tym modułem,
// więc `window.MexIcons` istnieje już przy pierwszym wykonaniu main.ts.
declare global {
  interface Window { MexIcons?: { svg(name: string, opts?: { size?: number }): string } }
}
/** Ikona z rodzinnego zbioru, albo pusty string, gdy skrypt się nie wczytał
 * (np. stary cache SW) — lepszy brak ikony niż wyjątek blokujący cały ekran. */
const icon = (name: string, size: number): string => window.MexIcons?.svg(name, { size }) ?? "";

const $ = (id: string) => document.getElementById(id)!;
const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
const zl = (gr: number) => `${(gr / 100).toFixed(2).replace(".", ",")} zł`;

let lastOffers: OfferResult[] = [];
let lastArgs: { fromUic: string; toUic: string; trainId: string | null;
  travelAt: string | null; passengers: Passenger[] } | null = null;

// ── ReadinessBar ─────────────────────────────────────────────────────────────

let rbarOpen = false;
let lastReadiness: Readiness | null = null;

// Phosphor prohibit/check-circle/warning (handoff „Light redesign” §2a) zamiast
// znaków „✓/!/✕" — stan czyta się po KOLORZE i KSZTAŁCIE ikony, nie po glifie.
const RBAR_ICON_NAME: Record<Readiness["state"], string> = {
  ok: "check-circle", warn: "warning", bad: "prohibit",
};

/** Jedno zdanie: czy mogę sprzedawać. Zastępuje dawne sześć plakietek obok dwóch
 * przycisków (audyt designu 2026-10-06) — szczegóły (taryfa/pula/transakcje/sync)
 * są rozwijane kliknięciem, nie widoczne na stałe. */
async function drawReadinessBar(): Promise<void> {
  const r = await computeReadiness(() => doSync());
  lastReadiness = r;
  const wrap = $("rbar-wrap");
  wrap.innerHTML = `
    <div class="rbar rbar--${r.state}${rbarOpen ? " open" : ""}" id="rbar-head">
      <span class="rbar__icon">${icon(RBAR_ICON_NAME[r.state], 24)}</span>
      <span class="rbar__txt">
        <div class="rbar__title">${esc(r.title)}</div>
        <div class="rbar__sub">${esc(r.subtitle)}</div>
      </span>
      <span class="rbar__caret">▾</span>
    </div>
    <div class="rbar__detail${rbarOpen ? " open" : ""}" id="rbar-detail">
      ${r.rows.map((row) => `<div class="rbar__row">
        <span class="rbar__row-ic ${row.state}">${icon(RBAR_ICON_NAME[row.state], 18)}</span>
        <span class="rbar__row-txt">
          <div class="rbar__row-title">${esc(row.title)}</div>
          <div class="rbar__row-sub">${esc(row.subtitle)}</div>
        </span>
      </div>`).join("")}
      <label class="rbar__force"><input type="checkbox" id="force-off"
        ${api.isForcedOffline() ? "checked" : ""}> Wymuś offline (tryb deweloperski)</label>
      ${r.cta ? `<button class="rbar__cta" id="rbar-cta">${esc(r.cta.label)}</button>` : ""}
    </div>`;
  $("rbar-head").onclick = () => { rbarOpen = !rbarOpen; void drawReadinessBar(); };
  ($("force-off") as HTMLInputElement).onchange = (e) => {
    api.forceOffline((e.target as HTMLInputElement).checked);
    void drawReadinessBar();
  };
  if (r.cta) $("rbar-cta").onclick = (ev) => { ev.stopPropagation(); void r.cta!.run(); };
}

// Kłódka to ikona, nie emoji 🔒 (handoff §2a) — pole dotyku zostaje 44×44 (CSS).
$("btn-lock").innerHTML = icon("lock-simple", 20);
$("btn-lock").onclick = () => { vault.lock(); void render(); };

$("btn-qr-login").innerHTML = icon("qr-code", 20);
$("btn-qr-login").onclick = () => void openPanelLoginScan();

// ── Skaner QR, wspólny dla dwóch NIEZALEŻNYCH funkcji ───────────────────────
//
// (1) logowanie panelu SaleLight (ten telefon jest już uwierzytelniony, skan
//     PRZENOSI jego tożsamość konduktora do cudzej przeglądarki) i
// (2) provisioning TEGO urządzenia (ekran „Konfiguracja", PRZED jakąkolwiek
//     tożsamością — skan wypełnia klucz API kanału i Id urządzenia zamiast
//     ręcznego przepisywania). Nadkładka kamery i pętla dekodowania są te
//     same; różni je tylko `onDecode` i to, co rozpoznają w payloadzie.
let qrStream: MediaStream | null = null;
let qrRaf = 0;

function closeQrScanner(): void {
  if (qrRaf) cancelAnimationFrame(qrRaf);
  qrRaf = 0;
  qrStream?.getTracks().forEach((t) => t.stop());
  qrStream = null;
  $("qr-login-overlay").classList.add("hidden");
  $("qr-login-overlay").innerHTML = "";
}

/** Rozpoznaje ZARÓWNO JSON ze skanu (`{t,v,code}`), JAK I gołą wartość wpisaną
 * ręcznie w pole tekstowe (bez koperty — skan bywa zawodny, kod krótki ma być
 * też wpisywalny, jak parowanie Netflixa). Kod wpisany ręcznie nie niesie
 * znacznika `t`, więc ewentualna pomyłka (wpisanie kodu z NIEWŁAŚCIWEGO ekranu)
 * i tak kończy się czystą odmową serwera (kod nieznany w tej tabeli), a nie
 * cichym pomieszaniem dwóch funkcji. */
function extractCode(tresc: string, expectedType: string): string | null {
  const trimmed = tresc.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("{")) {
    try {
      const d = JSON.parse(trimmed) as { t?: string; v?: number; code?: string };
      return d.t === expectedType && d.code ? d.code : null;
    } catch { return null; }
  }
  return trimmed.toUpperCase().replace(/[\s-]/g, "");
}

/** `onDecode` zwraca `true`, gdy skan/wpis się UDAŁ (overlay zamyka się sam po
 * chwili) — `false` zostawia kamerę i pole ręczne aktywne, żeby pomyłkowa albo
 * nietrafiona próba nie kończyła całej sesji. Kamera i ręczne pole DZIELĄ
 * jedną blokadę (`busy`) — inaczej dałoby się wysłać dwie równoległe próby
 * tym samym kodem.
 *
 * Pole ręczne jest WIDOCZNE OD RAZU, aparat to opcja na żądanie (przycisk
 * „Zeskanuj aparatem”) — nie startuje się sam. Zgłoszone na żywo: natywne
 * pytanie przeglądarki o uprawnienia do kamery potrafi zablokować interakcję
 * z resztą strony, więc pole, które „już tam jest, tylko nieosiągalne, dopóki
 * nie odpowiesz na pytanie o aparat” w praktyce w ogóle się nie otwierało. */
async function openQrScanner(
  instrukcja: string,
  onDecode: (tresc: string, status: HTMLElement) => Promise<boolean>,
): Promise<void> {
  const overlay = $("qr-login-overlay");
  overlay.innerHTML = `
    <div class="qrl-card">
      <button class="qrl-close" id="qrl-close">${icon("x", 20) || "✕"}</button>
      <div class="qrl-status" id="qrl-status">${esc(instrukcja)}</div>
      <div class="qrl-manual">
        <input id="qrl-manual-input" placeholder="wpisz kod (np. H3K9PX)"
          maxlength="8" autocapitalize="characters" autocomplete="off" autocorrect="off">
        <button id="qrl-manual-go">Potwierdź</button>
      </div>
      <button id="qrl-cam-go" class="qrl-cam-btn">Zeskanuj aparatem</button>
      <video id="qrl-video" playsinline muted class="hidden"></video>
    </div>`;
  overlay.classList.remove("hidden");
  $("qrl-close").onclick = closeQrScanner;
  const status = $("qrl-status");
  let busy = false; // jedno potwierdzenie na raz — kamera i pole ręczne czekają na wynik

  const submit = (tresc: string): void => {
    if (busy) return;
    busy = true;
    void onDecode(tresc, status).then((udalo) => {
      if (udalo) { setTimeout(closeQrScanner, 1200); return; }
      busy = false;
    });
  };
  $("qrl-manual-go").onclick = () => {
    const v = ($("qrl-manual-input") as HTMLInputElement).value;
    if (v.trim()) submit(v);
  };
  $("qrl-manual-input").addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") $("qrl-manual-go").click();
  });

  $("qrl-cam-go").onclick = () => void startCamera();

  async function startCamera(): Promise<void> {
    const przycisk = $("qrl-cam-go") as HTMLButtonElement;
    const video = $("qrl-video") as HTMLVideoElement;
    przycisk.disabled = true;
    przycisk.textContent = "Uruchamianie aparatu…";
    try {
      qrStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
    } catch (e) {
      // Pole ręczne działało od razu i działa nadal — brak aparatu nie blokuje
      // całej funkcji, tylko jedną z dwóch dróg do tego samego kodu.
      przycisk.disabled = false;
      przycisk.textContent = "Zeskanuj aparatem";
      status.textContent = `Brak dostępu do aparatu: ${(e as Error).message}`;
      return;
    }
    przycisk.classList.add("hidden");
    video.classList.remove("hidden");
    video.srcObject = qrStream;
    await video.play();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const tick = (): void => {
      if (!qrStream) return; // overlay zamknięty w międzyczasie
      if (busy || video.readyState !== video.HAVE_ENOUGH_DATA) {
        qrRaf = requestAnimationFrame(tick);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const wynik = jsQR(frame.data, frame.width, frame.height);
      if (wynik) submit(wynik.data);
      qrRaf = requestAnimationFrame(tick);
    };
    qrRaf = requestAnimationFrame(tick);
  }
}

// ── (1) Logowanie panelu SaleLight kodem QR ─────────────────────────────────
//
// Podpis jest kluczem urządzenia (ten sam ES256, co na bilecie), payload
// niesie `code` z zeskanowanego QR, `uzyt` konduktora i znacznik czasu —
// serwer wiąże podpis z KONKRETNYM kodem logowania, więc potwierdzenia nie da
// się podstawić pod inną, równoległą próbę logowania na tym samym urządzeniu.
async function openPanelLoginScan(): Promise<void> {
  const cfg = await api.config();
  if (!cfg?.conductor) { alert("Zaloguj się najpierw na tym urządzeniu (konduktor)."); return; }
  await openQrScanner(
    "Nakieruj na kod QR z ekranu logowania SaleLight…",
    (tresc, status) => confirmPanelLogin(tresc, cfg, status),
  );
}

async function confirmPanelLogin(
  tresc: string, cfg: api.Config, status: HTMLElement,
): Promise<boolean> {
  const kod = extractCode(tresc, "salelight-qr-login");
  if (!kod) {
    status.textContent = "To nie jest kod logowania SaleLight — spróbuj innego.";
    return false; // kamera/pole zostają aktywne — pomyłka nie ma kończyć sesji
  }
  status.textContent = "Potwierdzanie…";
  try {
    const token = await vault.signTicketToken(
      { code: kod, uzyt: cfg.conductor!.uzyt, ts: new Date().toISOString() },
      cfg.deviceId,
    );
    await api.online("/auth/qr/" + encodeURIComponent(kod) + "/confirm", { body: { token } });
    status.textContent = "Zalogowano panel SaleLight.";
    return true;
  } catch (e) {
    status.textContent = `Nie udało się potwierdzić: ${(e as Error).message}`;
    return false;
  }
}

// ── (2) Provisioning TEGO urządzenia kodem QR z listy „Urządzenia" panelu ───
//
// Kod niesie WYŁĄCZNIE opakowany, jednorazowy identyfikator — klucz API
// kanału i Id urządzenia wychodzą dopiero z wymiany na serwerze
// (`/auth/device-provision/{code}/claim`), żeby zdjęcie ekranu z kodem nie
// było zdjęciem sekretu wielokrotnego użytku. Po udanej wymianie formularz
// wypełnia się sam i od razu idzie dalej TĄ SAMĄ ścieżką co ręczne wpisanie
// (przycisk „Zarejestruj urządzenie") — żadnej drugiej implementacji enrollu.
async function openProvisionScan(): Promise<void> {
  await openQrScanner(
    "Nakieruj na kod urządzenia pokazany w panelu SaleLight…",
    confirmProvisionScan,
  );
}

async function confirmProvisionScan(tresc: string, status: HTMLElement): Promise<boolean> {
  const kod = extractCode(tresc, "smartlight-provision");
  if (!kod) {
    status.textContent = "To nie jest kod urządzenia SmartLight — spróbuj innego.";
    return false;
  }
  status.textContent = "Odbieranie konfiguracji…";
  try {
    const r = await fetch(
      `${ADRES_SALELIGHT}/auth/device-provision/${encodeURIComponent(kod)}/claim`,
      { method: "POST" },
    );
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error((data as { detail?: string } | null)?.detail || `HTTP ${r.status}`);
    const { apiKey, deviceId } = data as { apiKey: string; deviceId: string };
    ($("cf-key") as HTMLInputElement).value = apiKey;
    ($("cf-dev") as HTMLInputElement).value = deviceId;
    status.textContent = "Odebrano — rejestrowanie urządzenia…";
    ($("cf-go") as HTMLButtonElement).click();
    return true;
  } catch (e) {
    status.textContent = `Nie udało się: ${(e as Error).message}`;
    return false;
  }
}

async function doSync(): Promise<void> {
  try {
    const r = await sync.syncAll();
    alert(`Synchronizacja: dosłane ${r.sent}, duplikaty ${r.duplicates}` +
      `${r.poolTopUp ? ", nowa pula numerów" : ""}` +
      `${r.bundleRefreshed ? ", świeży kompilat taryfy" : ", taryfa bez zmian"}` +
      // kroki nieobowiązkowe (stan urządzenia) nie przerywają synchronizacji,
      // ale ich milczące pominięcie ukrywałoby brak przydziałów limitów
      `${r.errors.length ? `\nNiepełna: ${r.errors.join("; ")}` : ""}`);
  } catch (e) {
    alert(`Synchronizacja nieudana: ${(e as Error).message}`);
  }
  await drawReadinessBar();
  await render();
}

// Adres SaleLighta jest WBUDOWYWANY w build (Vite podstawia `import.meta.env` w chwili
// budowania, nie uruchomienia), bo kasa jest statykiem bez serwera aplikacyjnego —
// nie ma skąd wziąć adresu przy starcie. Bez tego argumentu budowania podpowiedź
// wskazuje pętlę lokalną URZĄDZENIA kasjera, czyli nigdzie.
const ADRES_SALELIGHT = import.meta.env.VITE_SALELIGHT_URL || "http://127.0.0.1:8100";

// ── ekrany ───────────────────────────────────────────────────────────────────

async function screenSetup(): Promise<void> {
  $("main").innerHTML = `
    <div class="card touch-form"><h2>Konfiguracja urządzenia (raz, ONLINE)</h2>
      <p class="muted">ID urządzenia tworzy operator w panelu SaleLight
      (Urządzenia SmartLight). Klucz podpisu generuje się TUTAJ i nie opuszcza
      urządzenia — rejestrowany jest tylko klucz publiczny.</p>
      <label>Adres SaleLight</label><input id="cf-url" value="${ADRES_SALELIGHT}">
      <label>Klucz API kanału SmartLight</label><input id="cf-key">
      <label>ID urządzenia</label><input id="cf-dev">
      <div class="err" id="cf-err"></div>
    </div>
    <div class="footer-cta">
      <button class="card-cta" id="cf-scan" style="margin-bottom:8px;background:var(--surface,#fff);
        color:var(--p,#16324f);border:1px solid var(--line,#d3d7de)">Zeskanuj kod z panelu SaleLight</button>
      <button class="primary card-cta" id="cf-go">Zarejestruj urządzenie</button>
    </div>`;
  $("cf-scan").onclick = () => void openProvisionScan();
  $("cf-go").onclick = async () => {
    try {
      const cfg = {
        saleLightUrl: (($("cf-url") as HTMLInputElement).value || "").replace(/\/$/, ""),
        apiKey: ($("cf-key") as HTMLInputElement).value.trim(),
        deviceId: ($("cf-dev") as HTMLInputElement).value.trim(),
      };
      await kv.set("config", cfg);
      const jwk = await vault.devicePublicJwk();
      const r = await fetch(`${cfg.saleLightUrl}/osdm/devices/${cfg.deviceId}/enroll`, {
        method: "POST",
        headers: { "X-Api-Key": cfg.apiKey, "Content-Type": "application/json" },
        body: JSON.stringify({ public_key: jwk }),
      });
      // 409 znaczy „ten id ma już klucz" — ale NIE znaczy, że to nasz. Po ponownej
      // instalacji magazyn generuje NOWY klucz prywatny, więc bezwarunkowe przejście
      // dalej kończyło się sprzedażą, której nie da się dosłać (sync odrzuca podpis).
      // Rozstrzyga odcisk klucza publicznego, który serwer podaje w odmowie.
      if (r.status === 409) {
        const d = (await r.json()).detail as
          { message?: string; keyThumbprint?: string; sameKey?: boolean } | string;
        const nasz = typeof d === "object" && d.keyThumbprint
          ? d.keyThumbprint === await vault.deviceKeyThumbprint()
          : false;
        if (!nasz)
          throw new Error(typeof d === "string" ? d
            : d.message || "urządzenie ma już zarejestrowany inny klucz podpisu");
      } else if (!r.ok) {
        throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      }
      await render();
    } catch (e) { $("cf-err").textContent = (e as Error).message; }
  };
}

async function screenLogin(): Promise<void> {
  $("main").innerHTML = `
    <div class="card touch-form"><h2>Logowanie konduktora (ONLINE)</h2>
      <p class="muted">Konto zarządzane w SaleLight (słownik Grupy sprzedaży).
      Po zalogowaniu ustalisz PIN — offline odblokowuje aplikację właśnie PIN.</p>
      <label>UZYT</label><input id="lg-uzyt" type="number">
      <label>Hasło</label><input id="lg-pass" type="password">
      <label>Nowy PIN (min. 4 cyfry)</label><input id="lg-pin" type="password">
      <div class="err" id="lg-err"></div>
    </div>
    <div class="footer-cta"><button class="primary card-cta" id="lg-go">Zaloguj i ustaw PIN</button></div>`;
  $("lg-go").onclick = async () => {
    try {
      const pin = ($("lg-pin") as HTMLInputElement).value;
      if (pin.length < 4) throw new Error("PIN musi mieć co najmniej 4 znaki");
      const r = await api.online<{ uzyt: number; name: string | null }>(
        "/auth/conductor-login", { body: {
          uzyt: Number(($("lg-uzyt") as HTMLInputElement).value),
          password: ($("lg-pass") as HTMLInputElement).value,
        } });
      const cfg = (await api.config())!;
      await kv.set("config", { ...cfg, conductor: r.data });
      await vault.setPin(pin);
      await doSync(); // pierwsza synchronizacja: pula + kompilat + rozliczenia
      await render();
    } catch (e) { $("lg-err").textContent = (e as Error).message; }
  };
}

async function screenPin(): Promise<void> {
  const left = await vault.pinAttemptsLeft();
  $("pin-overlay").classList.remove("hidden");
  $("pin-overlay").innerHTML = `
    <div class="card touch-form"><h2>Aplikacja zablokowana</h2>
      <label>PIN</label><input id="pin-in" type="password" autofocus>
      <div class="err" id="pin-err"></div>
      <p class="muted">Pozostało prób: ${left}. Po ich wyczerpaniu dane lokalne
      zostaną wymazane i konieczne będzie logowanie ONLINE.</p>
      <button class="primary card-cta" id="pin-go">Odblokuj</button>
    </div>`;
  $("pin-go").onclick = async () => {
    const wynik = await vault.unlock(($("pin-in") as HTMLInputElement).value);
    if (wynik === "ok") { $("pin-overlay").classList.add("hidden"); await render(); }
    else if (wynik === "wiped") { alert("Dane wymazane po błędnych PIN — zaloguj się online."); await render(); }
    else $("pin-err").textContent = `Zły PIN (pozostało ${await vault.pinAttemptsLeft()})`;
  };
}

function passengerRows(): string {
  return `<div id="pax-rows"></div>
    <button id="pax-add">+ pasażer</button>`;
}
function addPaxRow(): void {
  const div = document.createElement("div");
  div.className = "pax";
  div.innerHTML = `wiek <input class="pax-age" type="number" size="4" placeholder="—">
    data ur. <input class="pax-dob" type="date" title="legitymacja pokazuje DATĘ, nie wiek — wiek liczymy z niej na dzień podróży; wpisany wiek ma pierwszeństwo">
    kategoria <input class="pax-cat" size="10" placeholder="np. STUDENT">
    karty <input class="pax-cards" size="8" placeholder="np. IR">
    <button class="pax-del">✕</button>`;
  (div.querySelector(".pax-del") as HTMLButtonElement).onclick = () => div.remove();
  $("pax-rows").appendChild(div);
}
function readPassengers(): Passenger[] {
  return [...document.querySelectorAll("#pax-rows .pax")].map((row) => ({
    age: (row.querySelector(".pax-age") as HTMLInputElement).value
      ? Number((row.querySelector(".pax-age") as HTMLInputElement).value) : null,
    date_of_birth: (row.querySelector(".pax-dob") as HTMLInputElement).value || null,
    category_codes: (row.querySelector(".pax-cat") as HTMLInputElement).value
      .split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
    cards: (row.querySelector(".pax-cards") as HTMLInputElement).value
      .split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
  }));
}

async function screenSale(): Promise<void> {
  // Werdykt jest TEN SAM, który właśnie narysował ReadinessBar nad formularzem —
  // dwa miejsca liczące z osobna „czy wolno sprzedawać" to dokładnie błąd, który
  // ReadinessBar miał zlikwidować (audyt designu 2026-10-06).
  const r = lastReadiness ?? await computeReadiness(() => doSync());
  const blokada = r.state === "bad" ? r.subtitle : null;
  const bundle = await kv.get<Bundle>("bundle");
  const stations = bundle
    ? Object.entries(bundle.stations)
      .sort((a, b) => a[1].name.localeCompare(b[1].name, "pl"))
      .map(([uic, s]) => `<option value="${uic}">${esc(s.name)}</option>`).join("")
    : "";
  $("main").innerHTML = `
    <div class="card"><h2>Sprzedaż</h2>
      ${blokada ? `<p class="err">Sprzedaż wstrzymana: ${esc(blokada)}</p>`
        : r.state === "warn" ? `<p class="err" style="color:var(--warn-fg,#92400e)">${esc(r.subtitle)}</p>` : ""}
      <label>Skąd</label><select id="s-from">${stations}</select>
      <label>Dokąd</label><select id="s-to">${stations}</select>
      <label>Numer kursu (opcjonalnie — taryfy kursowe)</label><input id="s-train" size="8">
      <label>Wyjazd</label><input id="s-travel" type="datetime-local">
      <details id="s-hon"><summary>Dalsza część trasy u innego przewoźnika
        (honorowanie)</summary>
        <p class="muted">Wypełnij, gdy pasażer jedzie dalej pociągiem INNEGO przewoźnika.
          Bilet na całość powstanie tylko wtedy, gdy ten przewoźnik honoruje bilety
          oferty — inaczej wycena odmówi i powie, dlaczego.</p>
        <label>Przesiadka w</label><select id="s-hon-at"><option value="">—</option>${stations}</select>
        <label>RICS przewoźnika dalszej nogi</label><input id="s-hon-rics" size="6"
          placeholder="np. 5151">
        <label>Numer kursu dalszej nogi (opcjonalnie)</label><input id="s-hon-train" size="8">
      </details>
      <label>Pasażerowie</label>${passengerRows()}
      <label>Płatność</label>
      <select id="s-pay"><option value="0">gotówka</option>
        <option value="1">karta (terminal zewnętrzny)</option></select>
      <p><button class="primary" id="s-quote">Wyceń</button></p>
      <div id="s-offers"></div>
      <div id="s-tickets" class="tickets"></div>
    </div>`;
  $("pax-add").onclick = addPaxRow;
  addPaxRow();
  // przycisk martwy bez powodu jest nie do odróżnienia od awarii — powód stoi
  // nad formularzem, a przycisk tylko mu nie przeczy
  ($("s-quote") as HTMLButtonElement).disabled = Boolean(blokada);
  $("s-quote").onclick = () => void doQuote();
}

async function doQuote(): Promise<void> {
  const passengers = readPassengers();
  lastArgs = {
    fromUic: ($("s-from") as HTMLSelectElement).value,
    toUic: ($("s-to") as HTMLSelectElement).value,
    trainId: ($("s-train") as HTMLInputElement).value.trim() || null,
    travelAt: ($("s-travel") as HTMLInputElement).value || null,
    passengers,
  };
  // Nogi podróży: gdy kasjer wskazał przesiadkę do innego przewoźnika, wysyłamy DWIE
  // nogi (nasza do przesiadki, jego dalej) — inaczej żadnej, bo całość jedzie nasz
  // przewoźnik i honorowania nie ma co sprawdzać.
  const honAt = ($("s-hon-at") as HTMLSelectElement | null)?.value || "";
  const honRics = ($("s-hon-rics") as HTMLInputElement | null)?.value.trim() || "";
  const legs = honAt && honRics ? [
    // pierwsza noga BEZ przewoźnika: „nie wiemy" znaczy przewoźnik taryfy (fail-open,
    // ta sama reguła co online), więc nie musimy tu powtarzać własnego RICS
    { from_uic: lastArgs.fromUic, to_uic: honAt, carrier_rics: null,
      train_number: lastArgs.trainId },
    { from_uic: honAt, to_uic: lastArgs.toUic, carrier_rics: honRics,
      train_number: ($("s-hon-train") as HTMLInputElement | null)?.value.trim() || null },
  ] : [];
  try {
    const d = await api.osdm("/osdm/offers", {
      from_uic: lastArgs.fromUic, to_uic: lastArgs.toUic,
      train_id: lastArgs.trainId, travel_at: lastArgs.travelAt, passengers,
      legs: legs.length ? legs : undefined,
    }) as { offers: { offerSummary: { code: string; name: string; localOfferId: string | null;
        ticketType: string; ticketCount: number };
      price: { amountGr: number }; lines: OfferResult["lines"];
      tickets: OfferResult["tickets"]; validityHours: number | null;
      validityUntil: string | null;
      honouredLegs: OfferResult["honouredLegs"] | null }[];
      unavailable: { code: string; reason: string }[];
      osdmIgnored?: { passengerDateOfBirth?: string[] } };
    lastOffers = d.offers.map((o) => ({
      code: o.offerSummary.code, name: o.offerSummary.name,
      localOfferId: o.offerSummary.localOfferId, ticketType: o.offerSummary.ticketType,
      totalGr: o.price.amountGr, lines: o.lines, tickets: o.tickets,
      validityHours: o.validityHours ?? null, validityUntil: o.validityUntil ?? null,
      honouredLegs: o.honouredLegs ?? [],
    }));
    $("s-offers").innerHTML = lastOffers.map((o, i) => `
      <div class="offer-row">
        <span><b>${esc(o.name)}</b> <span class="muted">${esc(o.code)}
          ${o.localOfferId ? `· OFERTA ${esc(o.localOfferId)}` : ""}
          · ${o.tickets.length} bilet(y)</span>
          ${o.honouredLegs.map((h) => `<div class="ok" title="${esc(h.note ?? "")}">
            ✓ ważny także w pociągach ${esc(h.carrierName ?? h.carrierRics)}:
            ${esc(h.fromName ?? h.fromUic)} – ${esc(h.toName ?? h.toUic)}</div>`).join("")}</span>
        <span><b>${zl(o.totalGr)}</b>
          <button data-i="${i}" class="s-buy">Sprzedaj</button></span>
      </div>`).join("") || '<p class="muted">Brak ofert dla tej relacji.</p>';
    // Ostrzeżenie o nieprzeliczonej dacie jest WIDOCZNE, nie schowane w „odmowach":
    // kasjer wpisał datę urodzenia i dostał cenę pełnopłatną, a to jest dokładnie
    // ta cicha zmiana ceny, której w tym repo nie wolno przemilczeć.
    const bezWieku = d.osdmIgnored?.passengerDateOfBirth ?? [];
    if (bezWieku.length)
      $("s-offers").innerHTML += `<p class="err">⚠ pozycje ${esc(bezWieku.join(", "))}:
        z podanej daty urodzenia NIE DA SIĘ wyprowadzić wieku — brakuje daty podróży
        albo data jest nieczytelna. Ulga zależna od wieku NIE została zastosowana;
        podaj datę podróży albo wpisz wiek wprost.</p>`;
    if (d.unavailable?.length)
      $("s-offers").innerHTML += `<details><summary class="muted">odmowy taryfy
        (${d.unavailable.length})</summary>${d.unavailable.map((u) =>
          `<div class="muted">✗ ${esc(u.code)} — ${esc(u.reason)}</div>`).join("")}</details>`;
    document.querySelectorAll<HTMLButtonElement>(".s-buy").forEach((b) =>
      b.onclick = () => void doSell(Number(b.dataset.i)));
  } catch (e) {
    $("s-offers").innerHTML = `<p class="err">${esc((e as Error).message)}</p>`;
  }
}

async function doSell(i: number): Promise<void> {
  if (!lastArgs) return;
  const offer = lastOffers[i];
  try {
    const r = await api.osdm("/osdm/offline-fulfill", {
      offer, fromUic: lastArgs.fromUic, toUic: lastArgs.toUic,
      trainId: lastArgs.trainId, travelAt: lastArgs.travelAt,
      passengers: lastArgs.passengers,
      sposOpl: Number(($("s-pay") as HTMLSelectElement).value),
    }) as { localUuid: string; tickets: sale.IssuedTicket[]; saleAt: string };
    const blocks = await Promise.all(r.tickets.map(async (t) => {
      const png = await QRCode.toDataURL(t.token, { margin: 1, width: 180 });
      return `<div style="margin:10px 0"><b>${esc(t.seria)} ${t.nr_bil}</b>
        · ${esc(t.offer_name)} · ${zl(t.price_gr)}<br>
        <img src="${png}" alt="kod 2D biletu ${esc(t.seria)} ${t.nr_bil}"></div>`;
    }));
    $("s-tickets").innerHTML = `<h3>Wystawione bilety (offline, transakcja
      ${esc(r.localUuid.slice(0, 8))}…)</h3>` + blocks.join("") +
      `<p class="muted">Transakcja czeka na synchronizację — paragon fiskalny
      powstanie przy dosłaniu do SaleLight.</p>
      <p><button onclick="window.print()">Drukuj</button></p>`;
    await drawReadinessBar();
  } catch (e) {
    alert(`Sprzedaż nieudana: ${(e as Error).message}`);
  }
}

// ── router ───────────────────────────────────────────────────────────────────

async function render(): Promise<void> {
  await drawReadinessBar();
  const cfg = await api.config();
  if (!cfg?.apiKey || !cfg.deviceId) return screenSetup();
  const hasVault = await kv.get("vault");
  if (!cfg.conductor || !hasVault) return screenLogin();
  if (!vault.isUnlocked()) return screenPin();
  $("pin-overlay").classList.add("hidden");
  return screenSale();
}

// blokada przy schowaniu aplikacji (wymóg: po zablokowaniu offline — PIN)
document.addEventListener("visibilitychange", () => {
  if (document.hidden && vault.isUnlocked()) { vault.lock(); void render(); }
});
window.addEventListener("online", () => {
  void drawReadinessBar();
  // odzyskana łączność = pierwsza okazja, żeby powiedzieć operatorowi, ile
  // sprzedaży czeka na tym urządzeniu (pełna synchronizacja wymaga PIN-u kasjera)
  void sync.reportPending();
});
window.addEventListener("offline", () => void drawReadinessBar());
if ("serviceWorker" in navigator) void navigator.serviceWorker.register("/sw.js");
void txns.all().then(() => render()); // otwarcie bazy przy starcie + pierwszy render
